import { SenderConfiguration } from '@/features/senders/types/sender.types';
import { useSenderConfiguration } from '@/hooks/useSenderConfiguration';
import { getPriorityColor } from '@/utils/sender-config';
import {
  AlertCircle,
  CheckCircle,
  Mail,
  Settings,
  XCircle,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface SenderConfigDisplayProps {
  senderConfigurations?: SenderConfiguration[];
  onConfigure: () => void;
  showConfigureButton?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

export const SenderConfigDisplay = ({
  senderConfigurations,
  onConfigure,
  showConfigureButton = true,
  size = 'md',
}: SenderConfigDisplayProps) => {
  const { t } = useTranslation();
  const { activeConfig, configInfo, hasConfiguration } =
    useSenderConfiguration(senderConfigurations);

  const getStatusIcon = () => {
    if (!hasConfiguration)
      return <XCircle className="h-4 w-4 text-neutral-400" />;

    switch (configInfo.color) {
      case 'success':
        return <CheckCircle className="text-success-500 h-4 w-4" />;
      case 'warning':
        return <AlertCircle className="text-warning-500 h-4 w-4" />;
      case 'error':
        return <XCircle className="text-error-500 h-4 w-4" />;
      default:
        return <XCircle className="h-4 w-4 text-neutral-400" />;
    }
  };

  const getSizeClasses = () => {
    switch (size) {
      case 'sm':
        return {
          container: 'text-sm',
          icon: 'h-3 w-3',
          button: 'px-2 py-1 text-xs',
        };
      case 'lg':
        return {
          container: 'text-base',
          icon: 'h-5 w-5',
          button: 'px-4 py-2 text-sm',
        };
      default:
        return {
          container: 'text-sm',
          icon: 'h-4 w-4',
          button: 'px-3 py-1.5 text-sm',
        };
    }
  };

  const sizeClasses = getSizeClasses();

  return (
    <div
      className={`flex items-center justify-between ${sizeClasses.container}`}
    >
      <div className="flex items-center gap-2">
        <Mail className={`${sizeClasses.icon} text-neutral-400`} />

        {hasConfiguration ? (
          <div className="flex items-center gap-2">
            {getStatusIcon()}
            <div className="flex flex-col">
              <span className="font-medium text-neutral-900">
                {configInfo.sender?.name}
              </span>
              <span className="text-neutral-500">
                {configInfo.sender?.email}
              </span>
            </div>
            {activeConfig?.priority !== undefined && (
              <span
                className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${getPriorityColor(activeConfig.priority)}`}
              >
                {t(
                  `senderConfig.priority.${activeConfig.priority === 0 ? 'default' : activeConfig.priority === 1 ? 'domain' : 'folder'}`,
                  {
                    0: 'Défaut',
                    1: 'Domaine',
                    2: 'Dossier',
                  }[activeConfig.priority] || 'Inconnu'
                )}
              </span>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-2">
            {getStatusIcon()}
            <span className="text-neutral-500">
              {t('senderConfig.notConfigured', 'Non configuré')}
            </span>
          </div>
        )}
      </div>

      {showConfigureButton && (
        <button
          onClick={onConfigure}
          className={`inline-flex items-center gap-1 rounded-lg border border-neutral-300 px-3 py-1.5 text-sm font-medium text-neutral-700 transition-colors hover:border-neutral-400 hover:bg-neutral-50 ${sizeClasses.button}`}
        >
          <Settings className={`${sizeClasses.icon}`} />
          <span className="hidden sm:inline">
            {t('senderConfig.configure', 'Configurer')}
          </span>
          <span className="sm:hidden">
            {t('senderConfig.configure', 'Configurer')}
          </span>
        </button>
      )}
    </div>
  );
};
